import React from 'react';
import { ErrorValidator } from './CommonInputValidators';

export interface IInputElementBaseProps<T> {
  value: T;
  onChange: (value: T, e?: React.MouseEvent) => any;
  onBlur?: () => any;
  disabled?: boolean;
  name?: string;
  placeholder?: string;
}

export interface IBaseInputElement {
  isValid: () => boolean;
  setForcefullyInvalid: (value: boolean) => void;
  isPristine: () => boolean;
  isBlurred: () => boolean;
  clearValidation: () => void;
}

export type IWithValidationHOCProps<P, T> = P & {
  errorValidators?: () => ErrorValidator<T>[];
  onValidityChange?: (isValid: boolean) => void;
  watchExternalValues?: any[];
  forceValidation?: boolean;
};

export default function WithValidation<R, P extends IInputElementBaseProps<T>, T>(
  Component: React.RefForwardingComponent<R, P>
) {
  const withValidation = function (
    props: IWithValidationHOCProps<P, T>,
    ref: React.MutableRefObject<IBaseInputElement>
  ): JSX.Element {
    const { errorValidators, onValidityChange, watchExternalValues, ...otherProps } = props;
    const wrappedComponentProps: P = (otherProps as any) as P;
    // TODO: "as any as P" for now -> this thing here gave me a real headache, I'll improve it when possible.
    // This is the wrapped components props without the hoc-only props. We do not want to forward the hoc props to the wrapped component.

    const pristineClassName: string = 'pristine';
    const invalidClassName: string = 'invalid';

    const [isPristine, setPristine] = React.useState<boolean>(true);
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [isBlurred, setBlurred] = React.useState<boolean>(false);
    const [isForcefullyInvalid, setForcefullyInvalid] = React.useState<boolean>(false);

    React.useImperativeHandle(ref, () => ({
      isPristine: () => isPristine,
      isValid: () => !errorMessages.length,
      setForcefullyInvalid,
      isBlurred: () => isBlurred,
      clearValidation: () => {
        setPristine(true);
        setErrorMessages([]);
        setBlurred(false);
        setForcefullyInvalid(false);
      },
    }));

    React.useEffect(() => {
      if (isBlurred && isPristine) {
        setPristine(false);
      }
    }, [props.value]);

    React.useEffect(() => {
      if (errorValidators && errorValidators().length > 0) {
        setErrorMessages(
          errorValidators()
            .map(validator =>
              validator.validate(props.value) ? null : validator.getMessage(props.name || props.placeholder)
            )
            .filter(message => message !== null)
        );
      }
    }, [props.value, watchExternalValues]);

    React.useEffect(() => onValidityChange && onValidityChange(!errorMessages.length), [errorMessages]);

    const wrappedComponent: JSX.Element = <Component {...wrappedComponentProps}></Component>;

    if (errorValidators && errorValidators().length > 0) {
      return (
        <div
          className={`input-validator ${
            ((isBlurred || props.forceValidation) && errorMessages.length > 0) || isForcefullyInvalid
              ? invalidClassName
              : ''
          } ${isPristine ? pristineClassName : ''}`}
        >
          <div className="wrapped-component">
            {React.cloneElement(wrappedComponent, {
              onBlur: () => {
                setBlurred(true);
                if (props.onBlur) {
                  props.onBlur();
                }
              },
            })}
          </div>
          <div className="error-messages">
            {(isBlurred || props.forceValidation) &&
              errorMessages.length > 0 &&
              errorMessages.map(errorMessage => <div className="error-message">{errorMessage}</div>)}
          </div>
        </div>
      );
    } else {
      return wrappedComponent;
    }
  };

  return React.forwardRef<IBaseInputElement, IWithValidationHOCProps<P, T>>(withValidation);
}
